import {
  Alert,
  AlertDescription,
  AlertIcon,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

type CurrencyInputProps = {
  state?: number;
  updateState: (value?: number) => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  textAlign?: 'left' | 'right';
};

function parseFormattedStringToFloat(numberString: string) {
  const parseableNumberString = numberString.replace(/,/g, ''); // removes commas
  // @jendevelops if need to update precision, update the toFixed value
  const float = parseFloat(parseableNumberString);
  return float;
}

const formatWholeNumberString = (stringNum: string) => {
  // format number 1000000 to 1,234,567
  return stringNum
    .replace(/\D/g, '') // replace any non digit characters
    .replace(/^0+/, '') // remove any leading zeros (don't worry, we'll add one back later if decimal only)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ','); // adds commas for every thousands;
};

const formatFractionalNumberString = (stringNum: string) => {
  return stringNum.replace(/\D/g, ''); // replace any non digit characters
};

export function formatCurrency({
  value,
  requireFixedDecimal,
}: {
  value: string;
  requireFixedDecimal: boolean;
}): string {
  const newValue = `${value}`;

  // don't validate empty input
  if (newValue === '') {
    return newValue;
  }

  const decimalIndex = newValue.indexOf('.');
  const hasDecimal = decimalIndex >= 0;

  let leftSide =
    // if have decimal point, declare left side by decimal index
    hasDecimal ? newValue.substring(0, decimalIndex) : newValue;

  const validatedLeftSide = formatWholeNumberString(leftSide);
  // limit to digits + add commas to whole number side
  // this is where we add back in the leading 0 that's stripped out in formatWholeNumberString
  leftSide = validatedLeftSide === '' ? '0' : validatedLeftSide;

  let rightSide = hasDecimal ? newValue.substring(decimalIndex) : '';

  // limit fractional side to only digits
  const validatedRightSide = formatFractionalNumberString(rightSide);
  rightSide = hasDecimal ? `.${validatedRightSide}` : '';

  if (requireFixedDecimal) {
    // guarantees fractionalSide to 2 digits (only want to do on final formatting like onBlur so you don't get rid of the user's decimal input)
    // @jendevelops if we want to extend this to allow for more than 2 decimal places, we can add a prop to the component
    rightSide = formatFractionalNumberString(rightSide);

    const newRightSide = `${rightSide}00`.substring(0, 2);
    rightSide = `.${newRightSide}`;
  }
  return `${leftSide}${rightSide}`;
}

export const CurrencyInput = ({
  state,
  updateState,
  isDisabled = false,
  isReadOnly = false,
  textAlign = 'right',
}: CurrencyInputProps) => {
  const [text, setText] = useState<string>(
    state
      ? formatCurrency({
          value: String(state),
          requireFixedDecimal: false,
        })
      : '',
  );
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  useEffect(() => {
    const newState =
      typeof state === 'number'
        ? formatCurrency({
            value: String(state),
            requireFixedDecimal: true,
          })
        : '';
    setText(newState);
    clearError();
  }, [state, clearError]);

  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      const target = e.target as HTMLInputElement;
      const newValue = formatCurrency({
        value: target.value,
        requireFixedDecimal: true,
      });
      setText(newValue);

      if (newValue === '') {
        setError('Required');
        return;
      }
      const parsedValue = parseFormattedStringToFloat(newValue);

      if (!Number.isNaN(parsedValue)) {
        updateState(parsedValue);
      } else {
        setError('Invalid number');
      }
    },
    [setText, updateState],
  );

  return (
    <>
      <InputGroup w="100%" bgColor="white">
        <InputLeftElement pointerEvents="none">
          <Icon icon="dollar" />
        </InputLeftElement>
        <Input
          // type="number"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          type="text"
          pattern="^\$\d{1,3}(,\d{3})*(\.\d+)?$"
          isInvalid={Boolean(error)}
          value={text}
          onChange={(e) => {
            const newValue = e.target.value;
            // reset error until checked again in onBlur
            clearError();

            if (newValue !== '') {
              setText(
                formatCurrency({
                  value: newValue,
                  requireFixedDecimal: false,
                }),
              );
              return;
            }
            setText(newValue);
          }}
          onBlur={handleOnBlur}
          _invalid={{
            border: `1px solid ${colors.red[400]}`,
            boxShadow: 'none',
          }}
          sx={{
            textAlign,
          }}
          borderColor={colors.gray[100]}
        />
      </InputGroup>
      {error && (
        <Alert status="error" sx={{ mt: '1em' }}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </>
  );
};
